import React from 'react';
import { Link, Typography } from '@klp/design';
import type { Theme } from '@material-ui/core';
import MUIDataTable from 'mui-datatables';
import type { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { MenuPopper } from './MenuPopper/MenuPopper';
import { useHistory } from 'react-router-dom';
import { ProsjektRoutes } from '../config';
import {
    getProjectTypeKeyAbbreviation,
    getSuperofficeProsjektUrlByProsjektId,
} from '../utils';
import { REGISTRERING_STATUS } from '@klp/formgenerator/interfaces';
import { useCurrentUser } from '@klp/auth';
import classNames from 'classnames';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { useMapEmailToUser } from '../hooks';
import type { GodkjenningProsessSimple } from '../interfaces';
import { formatDate, parseSafeDate } from '@klp/utils/dateTimeFormatter';
import { FilterTreff } from '@klp/design/FilterPanel/FilterPanel';
import { visVerdi } from '@klp/utils/tableUtils';
import { useKolonnevalg } from '@forvalter/utils/useKolonnevalg';
import { listetabellStiler } from '@forvalter/utils/listetabellStiler';

type ProsjektProps = {
    prosjekter: Array<GodkjenningProsessSimple>;
};

// Define column metadata that's independent of runtime data
// This serves as the single source of truth for column configuration
const PROSJEKT_TABLE_COLUMN_METADATA = [
    { name: 'prosjektskjemaType', label: 'prosjekt.table.prosjektskjematype' },
    { name: 'type', label: 'prosjekt.table.type' },
    { name: 'd365Prosjektnummer', label: 'prosjekt.table.d365Prosjektnummer' },
    { name: 'byggnummer', label: 'prosjekt.table.eiendom' },
    { name: 'navn', label: 'prosjekt.table.navn' },
    { name: 'beskrivelse', label: 'prosjekt.table.beskrivelse' },
    { name: 'prosjektStatus', label: 'prosjekt.table.status' },
    { name: 'prosjekteier', label: 'prosjekt.table.prosjekteier' },
    { name: 'prosjektleder', label: 'prosjekt.table.prosjektleder' },
    { name: 'ansvarlig', label: 'prosjekt.table.ansvarlig' },
    { name: 'startDato', label: 'prosjekt.table.startDato' },
    { name: 'fullfortDato', label: 'prosjekt.table.fullfortDato' },
    { name: 'action', label: 'prosjekt.table.action' },
] as const;

// Use metadata as single source of truth for column names and labels
const COL = PROSJEKT_TABLE_COLUMN_METADATA;

// Remembers the sorting between visits. The chosen columns are kept by
// useKolonnevalg, since mui-datatables doesn't keep them for columns with a
// default.
const PROSJEKT_TABLE_STORAGE_KEY = 'prosjektTableState';
const PROSJEKT_TABLE_KOLONNER_KEY = 'prosjektTableKolonner';

// Hidden until the user shows them under «Vis kolonner». The number is also in
// the name.
const STANDARD_SKJULTE_KOLONNER = ['prosjektskjemaType', 'fullfortDato'];

const styles = (theme: Theme) => ({
    centerText: {
        textAlign: 'center',
    },
    table: {
        cursor: 'pointer',
        ...listetabellStiler(theme),
    },
    // The app theme draws the line of a link under the whole block, which is
    // the width of the cell when the name wraps. This follows the text instead.
    // Doubled, so it wins over the theme.
    navnLenke: {
        '&&': {
            textDecoration: 'underline',
            textDecorationColor: theme.palette.secondary.main,
            textDecorationThickness: '2px',
            textUnderlineOffset: '3px',
        },
        '&&:hover': {
            textDecorationColor: theme.palette.primary.main,
        },
        '&&:before': {
            display: 'none',
        },
    },
    tableBlurred: {
        // Elements, since MUI's class names get a generated suffix in this app
        '& tbody tr': {
            opacity: 0.4,
            filter: 'blur(1px)',
            transition: 'opacity 0.2s, filter 0.2s',
            pointerEvents: 'none',
        },
        '& tbody tr[data-active="true"]': {
            opacity: 1,
            filter: 'none',
            pointerEvents: 'auto',
            backgroundColor: theme.palette.secondary.veryLight,
        },
    },
});

type KolonneParams = {
    t: TFunction;
    prosjekter: Array<GodkjenningProsessSimple>;
    handleMapping: ReturnType<typeof useMapEmailToUser>['handleMapping'];
    isEDAdmin?: boolean;
    openPopperId: string | null;
    anchorEl: HTMLElement | null;
    handleTogglePopper: (
        prosessInstansId: string,
    ) => (event: React.MouseEvent<HTMLButtonElement>) => void;
    handleClosePopper: () => void;
    navnLenkeKlasse: string;
};

/** The table's columns, before the user's column choices are applied. */
const getKolonner = ({
    t,
    prosjekter,
    handleMapping,
    isEDAdmin,
    openPopperId,
    anchorEl,
    handleTogglePopper,
    handleClosePopper,
    navnLenkeKlasse,
}: KolonneParams): Array<MUIDataTableColumn> => [
    {
        label: t(COL[0].label),
        name: COL[0].name,
        options: {
            customBodyRender: (value) => {
                return visVerdi(value?.typeNavn);
            },
        },
    },
    {
        label: t(COL[1].label),
        name: COL[1].name,
        options: {
            customBodyRenderLite: (dataIndex) => {
                const type = getProjectTypeKeyAbbreviation(
                    prosjekter[dataIndex]?.type,
                );

                return (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {t(type)}
                    </Typography>
                );
            },
        },
    },
    {
        label: t(COL[2].label),
        name: COL[2].name,
        options: {
            setCellHeaderProps: () => ({
                style: { whiteSpace: 'nowrap' },
            }),
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(prosjekter[dataIndex]?.d365Prosjektnummer)}
                </Typography>
            ),
        },
    },
    {
        label: t(COL[3].label),
        name: COL[3].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(prosjekter[dataIndex]?.byggnummer)}
                </Typography>
            ),
        },
    },
    {
        label: t(COL[4].label),
        name: COL[4].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Link
                    klpTypographyVariant="small"
                    className={navnLenkeKlasse}
                    data-cy="Prosjekttable__navn"
                    klpColor="main"
                    title={t('prosjekt.table.aapneISuperOffice')}
                    aria-label={`${t('prosjekt.table.aapneISuperOffice')}: ${prosjekter[dataIndex]?.navn ?? ''}`}
                    href={
                        prosjekter[dataIndex]?.prosjektId
                            ? getSuperofficeProsjektUrlByProsjektId(
                                  prosjekter[dataIndex].prosjektId,
                              )
                            : undefined
                    }
                    target="_blank"
                    externalLink={true}
                    // Opens SuperOffice, not the skjema like the rest of the row
                    onClick={(event) => event.stopPropagation()}
                >
                    {prosjekter[dataIndex]?.navn}
                </Link>
            ),
        },
    },
    {
        label: t(COL[6].label),
        name: COL[6].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {t(
                        `prosjekt.statuses.${prosjekter[dataIndex]?.prosjektStatus}`,
                    )}
                </Typography>
            ),
        },
    },
    {
        label: t(COL[7].label),
        name: COL[7].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(
                        handleMapping(prosjekter[dataIndex]?.prosjekteier),
                    )}
                </Typography>
            ),
        },
    },
    {
        label: t(COL[8].label),
        name: COL[8].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(
                        handleMapping(prosjekter[dataIndex]?.prosjektleder),
                    )}
                </Typography>
            ),
        },
    },
    {
        label: t(COL[9].label),
        name: COL[9].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(handleMapping(prosjekter[dataIndex]?.ansvarlig))}
                </Typography>
            ),
        },
    },
    {
        label: t(COL[10].label),
        name: COL[10].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(
                        formatDate(
                            parseSafeDate(prosjekter[dataIndex]?.startDato),
                        ),
                    )}
                </Typography>
            ),
        },
    },
    {
        label: t(COL[11].label),
        name: COL[11].name,
        options: {
            customBodyRenderLite: (dataIndex) => (
                <Typography klpTypographyVariant="small" klpColor="main">
                    {visVerdi(
                        formatDate(
                            parseSafeDate(prosjekter[dataIndex]?.fullfortDato),
                        ),
                    )}
                </Typography>
            ),
        },
    },
    {
        label: t(COL[12].label),
        name: COL[12].name,
        options: {
            sort: false,
            customBodyRenderLite: (dataIndex) => {
                const prosjekt = prosjekter[dataIndex];

                if (!prosjekt) {
                    return null;
                }

                return (
                    <MenuPopper
                        prosjekt={prosjekt}
                        isEDAdmin={isEDAdmin}
                        open={openPopperId === prosjekt.prosessInstansId}
                        anchorEl={anchorEl}
                        onToggle={handleTogglePopper(prosjekt.prosessInstansId)}
                        onClose={handleClosePopper}
                    />
                );
            },
        },
    },
];

type TabellvalgParams = {
    t: TFunction;
    prosjekter: Array<GodkjenningProsessSimple>;
    openPopperId: string | null;
    onRowClick: NonNullable<MUIDataTableOptions['onRowClick']>;
    onViewColumnsChange: NonNullable<
        MUIDataTableOptions['onViewColumnsChange']
    >;
};

const getTabellvalg = ({
    t,
    prosjekter,
    openPopperId,
    onRowClick,
    onViewColumnsChange,
}: TabellvalgParams): MUIDataTableOptions => ({
    onRowClick,
    onViewColumnsChange,
    // The search field above the table searches in the backend instead
    search: false,
    download: false,
    print: false,
    viewColumns: true,
    // Filtering is done in the filter panel above the table
    filter: false,
    sort: true,
    storageKey: PROSJEKT_TABLE_STORAGE_KEY,
    selectableRows: 'none',
    textLabels: {
        body: {
            noMatch: t('prosjekt.table.emptyTableMessage'),
            toolTip: t('tables.body.toolTip'),
            columnHeaderTooltip: (column) =>
                t('tables.body.toolTipFor') + column.label,
        },
        pagination: {
            next: t('tables.pagination.next'),
            previous: t('tables.pagination.previous'),
            rowsPerPage: t('tables.pagination.rowsPerPage'),
            displayRows: t('tables.pagination.displayRows'),
        },
        toolbar: {
            downloadCsv: t('tables.toolbar.downloadCsv'),
            print: t('tables.toolbar.print'),
            viewColumns: t('tables.toolbar.viewColumns'),
        },
        viewColumns: {
            title: t('tables.viewColumns.title'),
            titleAria: t('tables.viewColumns.titleAria'),
        },
        selectedRows: {
            text: t('tables.selectedRows.text'),
            delete: t('tables.selectedRows.delete'),
            deleteAria: t('tables.selectedRows.deleteAria'),
        },
    },
    setRowProps: (row, dataIndex) => ({
        'data-active':
            openPopperId === prosjekter[dataIndex]?.prosessInstansId
                ? 'true'
                : 'false',
    }),
});

export const ProsjektTable: React.FC<ProsjektProps> = ({ prosjekter }) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { currentUser } = useCurrentUser();
    const { handleMapping } = useMapEmailToUser();
    const [openPopperId, setOpenPopperId] = React.useState<string | null>(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const { medKolonnevalg, onViewColumnsChange } = useKolonnevalg(
        PROSJEKT_TABLE_KOLONNER_KEY,
        STANDARD_SKJULTE_KOLONNER,
    );

    const handleTogglePopper = React.useCallback(
        (prosessInstansId: string) =>
            (event: React.MouseEvent<HTMLButtonElement>) => {
                if (openPopperId === prosessInstansId) {
                    setOpenPopperId(null);
                    setAnchorEl(null);
                } else {
                    setOpenPopperId(prosessInstansId);
                    setAnchorEl(event.currentTarget);
                }
            },
        [openPopperId],
    );

    const handleClosePopper = React.useCallback(() => {
        setOpenPopperId(null);
        setAnchorEl(null);
    }, []);

    const history = useHistory();

    const handleRowClick = React.useCallback(
        (
            rowData: Array<string>,
            rowMeta: { dataIndex: number; rowIndex: number },
        ) => {
            const prosjektId = prosjekter[rowMeta.dataIndex]?.prosjektId;
            const prosessInstansId =
                prosjekter[rowMeta.dataIndex]?.prosessInstansId;
            if (!prosjektId || !prosessInstansId) {
                return;
            }
            if (
                prosjekter[rowMeta.dataIndex]?.registreringStatus !==
                REGISTRERING_STATUS.NY
            ) {
                history.push(
                    ProsjektRoutes.projectSummary(prosessInstansId, prosjektId),
                );
            } else {
                history.push(
                    ProsjektRoutes.projectSchemaSingle(
                        prosessInstansId,
                        prosjektId,
                    ),
                    {
                        routeBackToSummaryPage: false,
                    },
                );
            }
        },
        [prosjekter, history],
    );

    // Memoized so the table doesn't rebuild its columns on every render
    const tableColumns = React.useMemo(
        () =>
            medKolonnevalg(
                getKolonner({
                    t,
                    prosjekter,
                    handleMapping,
                    isEDAdmin: currentUser?.isEDAdmin,
                    openPopperId,
                    anchorEl,
                    handleTogglePopper,
                    handleClosePopper,
                    navnLenkeKlasse: classes.navnLenke,
                }),
            ),
        [
            medKolonnevalg,
            t,
            prosjekter,
            handleMapping,
            currentUser?.isEDAdmin,
            openPopperId,
            anchorEl,
            handleTogglePopper,
            handleClosePopper,
            classes.navnLenke,
        ],
    );

    const tableOptions = React.useMemo(
        () =>
            getTabellvalg({
                t,
                prosjekter,
                openPopperId,
                onRowClick: handleRowClick,
                onViewColumnsChange,
            }),
        [t, prosjekter, openPopperId, handleRowClick, onViewColumnsChange],
    );

    return (
        <div
            className={classNames(classes.table, {
                [classes.tableBlurred]: openPopperId,
            })}
        >
            <MUIDataTable
                title={<FilterTreff antall={prosjekter.length} />}
                data={prosjekter ?? []}
                columns={tableColumns}
                options={tableOptions}
                data-testid="ProsjektToRegistrationTable__Table"
            />
        </div>
    );
};
